import { ReactElement, useState } from "react";
import {
  FormControl,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField, Tooltip,
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import { DashboardAggregateType, IDashboardVariable, IDataSchema } from "../types";
import { Add, Delete } from "@mui/icons-material";
import { getAggregationText } from "./utils";
import { getPalette } from "../../../configuration/configuration";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TableContainer from "../../../components/tableContainer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ConfigItemDialog from "./configItemDialog";
// import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
// import NorthIcon from "@mui/icons-material/North";
// import SouthIcon from "@mui/icons-material/South";


interface IProps {
  allowMultipleVariables: boolean;
  schema: IDataSchema;
  variables: IDashboardVariable[];
  updateVariables: (variables: IDashboardVariable[]) => void;
}

const SeriesTable = ({ variables, updateVariables, schema, allowMultipleVariables }: IProps): ReactElement => {
  const [ variable, setVariable ] = useState<IDashboardVariable | null>(null);

  const addSeries = () => {
    updateVariables([
      ...variables,
      {
        field: "",
        color: getPalette().list[variables.length],
        aggregate: DashboardAggregateType.None,
        unit: "",
        decimals: 0
      } ]);
  };

  const removeSeries = (index: number) => {
    const newArray = [ ...variables ];
    newArray.splice(index, 1);
    updateVariables(newArray);
  };

  const updateItemsSeries = (index: number, field: keyof IDashboardVariable, value: any) => {
    const newArray = [ ...variables ];
    const item = newArray[index] as any;

    if (field === "field") {
      const units =  schema.variables[value].units !== undefined ? schema.variables[value].units as string[] : [ "" ];

      item[field] = value;
      item.aggregate = schema.variables[value].aggregate[0];
      item.unit = units[0];
    } else {
      item[field] = value;
    }

    updateVariables(newArray);
  };

  const onCustomizeItem = (item: IDashboardVariable) => {
    setVariable(item);
  };

  const onAcceptCustom = () => {
    setVariable(null);
  };

  const onCancelCustom = () => {
    setVariable(null);
  };

  // const onOrderBy = (item: IDashboardVariable) => {
  //   variables.forEach(v => {
  //     if(!v.custom) {
  //       v.custom = {};
  //     }
  //
  //     if (item !== v){
  //       v.custom.order = 0;
  //     }
  //   });
  //
  //   if(!item.custom) {
  //     item.custom = { order: 1 };
  //   } else if (!item.custom.order) {
  //     item.custom.order = 1;
  //   } else {
  //     item.custom.order = item.custom.order === 1 ? -1 : 0;
  //   }
  //
  //   console.log(item.custom);
  //
  //   updateVariables([...variables]);
  // };

  return (
    <>
      <TableContainer sx={{ maxHeight: 315 }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell className="table-header" style={{ minWidth: 175, padding: "8px"  }}>
                {allowMultipleVariables &&
                  <IconButton sx={{ padding: "2px" }} onClick={addSeries} disabled={variables.length > 4} size={"small"}>
                    <Tooltip title={"Add series"}>
                      <Add style={{ color: getPalette().statusColor.info.background }}/>
                    </Tooltip>
                  </IconButton>
                }
                Field
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 150 }}>Aggregate</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Unit</TableCell>
              <TableCell className="table-header" style={{ minWidth: 90 }}>Decimals</TableCell>
              <TableCell className="table-header" style={{ minWidth: 90 }}>Config</TableCell>
              <TableCell className="table-header" style={{ minWidth: 50 }}/>
            </TableRow>
          </TableHead>
          <TableBody>
            {variables.map((row, index) => {
              let units: string[] = [ "" ];
              if (schema.variables[row.field] && schema.variables[row.field].units) {
                units = schema.variables[row.field].units ?? [];
              }

              let aggregate: DashboardAggregateType[] = [ DashboardAggregateType.None ];
              if (row.field !== "" && schema.variables[row.field]) {
                aggregate = schema.variables[row.field].aggregate ?? [];
              }

              return (
                <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`series-row-${index}`}>
                  <TableCell>
                    <Select
                      variant="standard"
                      className="w-100"
                      value={row.field}
                      displayEmpty={true}
                      onChange={(event: SelectChangeEvent) => updateItemsSeries(index, "field", event.target.value as string)}>
                      {Object.keys(schema.variables).map(field => <MenuItem key={`field-${index}-${field}`} value={field}>{field}</MenuItem>)}
                    </Select>
                  </TableCell>

                  <TableCell>
                    <FormControl className="w-100">
                      <Select
                        variant="standard"
                        value={row.aggregate}
                        displayEmpty={true}
                        onChange={(event: SelectChangeEvent<DashboardAggregateType>) => updateItemsSeries(index, "aggregate", event.target.value as DashboardAggregateType)}>
                        {aggregate.map((item, uIndex) => {
                          return <MenuItem key={`unit-${item}-${uIndex}`} value={item}>{getAggregationText(item)}</MenuItem>;
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    <FormControl className="w-100">
                      <Select
                        variant="standard"
                        value={row.unit ?? ""}
                        displayEmpty={true}
                        onChange={(event: SelectChangeEvent) => updateItemsSeries(index, "unit", event.target.value)}>
                        { units.map((unit, uIndex) => {
                          return <MenuItem key={`unit-${index}-${uIndex}`} value={unit}>{unit}</MenuItem>;
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    <FormControl className="w-100">
                      <TextField
                        variant="standard"
                        size="small"
                        value={row.decimals}
                        type="number"
                        slotProps={{ input: { inputProps: { min: 0, max: 6 } }}}
                        onChange={(event) => updateItemsSeries(index, "decimals",  parseInt(event.target.value as string, 10))}
                      />
                    </FormControl>
                  </TableCell>

                  <TableCell  sx={{ padding: "8px" }}>
                    <div className="display-flex-row">
                      <IconButton sx={{ padding: "2px" }} onClick={() => onCustomizeItem(row)} size={"small"}>
                        <Tooltip title={"Config"}>
                          <MoreVertIcon/>
                        </Tooltip>
                      </IconButton>

                      {/*<IconButton sx={{ padding: "2px" }} onClick={() => onOrderBy(row)} size={"small"}>*/}
                      {/*  <Tooltip title={"Remove series"}>*/}
                      {/*    <>*/}
                      {/*      { !row.custom || !row.custom.order && <SortByAlphaIcon/> }*/}
                      {/*      { row.custom && row.custom.order === 1 && <NorthIcon /> }*/}
                      {/*      { row.custom && row.custom.order === -1 && <SouthIcon /> }*/}
                      {/*    </>*/}
                      {/*  </Tooltip>*/}
                      {/*</IconButton>*/}
                    </div>
                  </TableCell>

                  <TableCell sx={{ padding: "8px" }}>
                    <IconButton sx={{ padding: "2px" }} onClick={() => removeSeries(index)} disabled={variables.length === 1} size={"small"}>
                      <Tooltip title={"Remove series"}>
                        <Delete style={{ color: getPalette().statusColor.error.background }}/>
                      </Tooltip>
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {variable && <ConfigItemDialog variable={variable} onAccept={onAcceptCustom} onCancel={onCancelCustom} />}
    </>
  );
};

export default SeriesTable;
